import { useState } from 'react';

/**
 * O número que passa do que a obra tem — a vírgula no lugar errado.
 *
 * Pilar de 99 m, bloco de 12 m, laje de 3 m de espessura: o domínio aceita (são positivos), e
 * o volume sai gigante sem ninguém ver por quê. Não é erro, e por isso não recusa: **pede para
 * conferir** — o aviso aparece assim que o número passa do limite, e o cadastro só vale no
 * segundo clique, com o mesmo número.
 */

/** Acima disto o pilar pede confirmação, em metros. */
export const ALTURA_DO_PILAR_MAXIMA = 30;
/** Idem a altura do bloco, em metros. */
export const ALTURA_DO_BLOCO_MAXIMA = 10;
/** Idem a espessura do radier e da laje, em metros. */
export const ESPESSURA_DA_LAJE_MAXIMA = 2;

/** `99` vira "99 m", `1,5` vira "1,5 m" — como a obra lê. */
function emMetros(valor: number): string {
  return `${String(Math.round(valor * 1000) / 1000).replace('.', ',')} m`;
}

/** O aviso de um valor acima do limite, ou nada. */
export function avisoDeValorGrande(
  valor: number | undefined,
  limite: number,
  confirmado: boolean,
): string | undefined {
  if (valor === undefined || !Number.isFinite(valor) || valor <= limite) return undefined;
  return confirmado
    ? `Confira: ${emMetros(valor)}. Confirmado — pode cadastrar.`
    : `Confira: ${emMetros(valor)}. Passa do que a obra costuma ter; clique de novo para confirmar.`;
}

/**
 * Pede a confirmação do número grande: o primeiro clique mostra o aviso, o segundo — com o
 * mesmo número — vale. Mudar o número desfaz a confirmação.
 */
export function useConfirmacaoDeValorGrande(
  valor: number | undefined,
  limite: number,
): {
  /** O aviso para mostrar ao lado do campo — ausente com o número dentro do limite. */
  readonly aviso: string | undefined;
  /** `true` quando o cadastro pode seguir; do contrário o aviso passou a pedir o segundo clique. */
  readonly confirmar: () => boolean;
  /** Esquece a confirmação — depois de cadastrar. */
  readonly limpar: () => void;
} {
  const [visto, setVisto] = useState<number | undefined>(undefined);
  const grande = valor !== undefined && Number.isFinite(valor) && valor > limite;
  const confirmado = grande && visto === valor;

  return {
    aviso: avisoDeValorGrande(valor, limite, confirmado),
    confirmar: () => {
      if (!grande) return true;
      if (confirmado) return true;
      setVisto(valor);
      return false;
    },
    limpar: () => setVisto(undefined),
  };
}
